import { Tooltip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { FC, useEffect, useRef } from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Input } from '../common/form/Input';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { VisitorPersonFields } from './VisitorPersonFields';
import { prefillPersonFromEmail } from './prefillPersonFromEmail';
import { companyService } from '@api/services/services';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';
import { useSupplierResolution } from './useSupplierResolution';
import {
  AddArea,
  AddButton,
  CardHead,
  CardHeadLeft,
  CountPill,
  IndexChip,
  PlusBadge,
  RemoveButton,
  Section,
  SupplierText,
  TitleRow,
  VisitorCard,
  VisitorList,
} from './InvitationVisitorsSection.styles';

/**
 * Visitor-row stored in the Formik form. Carries an optional `_uid` to provide
 * stable React keys; the field is stripped before submission.
 */
export interface VisitorFormRow {
  name?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  language?: string;
  pin?: string;
  _uid?: string;
}

/**
 * Minimal Formik shape that this component reads from / writes to. The actual
 * parent forms (InvitationCreateForm, VisitationAddOneTimeForm) carry many more
 * fields — they just need to be assignable to this shape at the configured
 * paths.
 */
export interface VisitorsFormShape {
  additionalVisitors?: VisitorFormRow[];
  visitorCompanyName?: string;
  // Primary visitor lives under either `responsiblePerson` or `primaryVisitor`
  // depending on the parent form; the component reads it via `primaryPath`.
  [key: string]: unknown;
}

interface Props {
  allowedLanguages?: string[];
  primaryPath?: string;
  additionalPath?: string;
  companyFieldName?: string;
  showPin?: boolean;
  onSupplierResolved?: (supplierId: string | undefined) => void;
}

const generateUid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const InvitationVisitorsSection: FC<Props> = ({
  allowedLanguages,
  primaryPath = 'responsiblePerson',
  additionalPath = 'additionalVisitors',
  companyFieldName = 'visitorCompanyName',
  showPin = false,
  onSupplierResolved,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<VisitorsFormShape>();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const { enqueueWarningSnackbar, enqueueErrorSnackbar } = useAppSnackbar();

  const primaryVisitor: VisitorFormRow | undefined = getIn(values, primaryPath);
  const additionalVisitorsRaw: VisitorFormRow[] | undefined = getIn(values, additionalPath);
  const additionalVisitors: VisitorFormRow[] = additionalVisitorsRaw ?? [];
  const companyName: string = getIn(values, companyFieldName) ?? '';
  const totalCount = 1 + additionalVisitors.length;

  const buildEmptyVisitor = (): VisitorFormRow => ({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    language: companyCustomization?.defaultLanguage ?? '',
    _uid: generateUid(),
  });

  // List of visitor emails that feed supplier resolution. React Compiler
  // memoizes this for us, so no manual useMemo is needed (a manual one
  // conflicts with the compiler — see `react-hooks/preserve-manual-memoization`).
  const primaryEmail = primaryVisitor?.email;
  const visitorEmails: string[] = [];
  if (primaryEmail) visitorEmails.push(primaryEmail);
  for (const v of additionalVisitors) {
    if (v.email) visitorEmails.push(v.email);
  }

  const { uniqueSupplierNames, uniqueSupplierIds, lookupSupplierName, recordSupplier } = useSupplierResolution(visitorEmails);

  const primarySupplier = lookupSupplierName(primaryVisitor?.email);
  const showSupplierPerCard = uniqueSupplierNames.length > 1;

  // Tracks the last value this effect wrote into the company field. We only
  // auto-fill while the current field value still matches our last write —
  // once the user edits the company name manually, `autoCompanyRef` stops
  // matching and we leave their input alone (even if suppliers change).
  const autoCompanyRef = useRef<string>('');

  // Auto-derive the visitor company name from resolved suppliers:
  //   0 suppliers → clear, 1 → use its name, >1 → localized "multiple" label.
  // Skipped entirely if the user has manually edited the field (see ref above).
  useEffect(() => {
    if (companyName !== autoCompanyRef.current) return;
    let next: string;
    if (uniqueSupplierNames.length === 0) next = '';
    else if (uniqueSupplierNames.length === 1) next = uniqueSupplierNames[0];
    else next = t('invitation.create.step4MultipleSuppliers');
    if (next === companyName) return;
    autoCompanyRef.current = next;
    setFieldValue(companyFieldName, next);
  }, [uniqueSupplierNames, companyName, companyFieldName, setFieldValue, t]);

  // Notify the parent of the resolved supplier id, but only when every visitor
  // points at the same supplier. Mixed/empty resolutions report `undefined`
  // so the parent can disable supplier-specific UI.
  useEffect(() => {
    if (!onSupplierResolved) return;
    if (uniqueSupplierIds.length === 1) {
      onSupplierResolved(uniqueSupplierIds[0]);
    } else {
      onSupplierResolved(undefined);
    }
  }, [uniqueSupplierIds, onSupplierResolved]);

  const normalizeEmail = (e: string | undefined): string | undefined =>
    e?.replace(/\s/g, '');

  // On email blur: try to prefill the row from a known person record. If a
  // match is found we also feed the supplier cache so the company-name effect
  // above can pick it up. The cache key is the *normalized* email so future
  // lookups against trimmed input still hit.
  const prefillVisitorFromEmail = async (path: string, email: string | undefined) => {
    const db = await prefillPersonFromEmail(email, setFieldValue, path);
    if (!db) return;
    recordSupplier(normalizeEmail(db.email) ?? normalizeEmail(email), db);
  };

  // On PIN blur: resolve a supplier employee by PIN and prefill the row.
  // The PIN itself is always cleared from the form afterwards (it is not
  // submitted, it is only a lookup token).
  const prefillVisitorFromPin = async (path: string, pin: string) => {
    const cleaned = pin?.trim();
    if (!cleaned) return;
    try {
      const res = await companyService.findSupplierEmployeeInCompany({ pin: cleaned });
      const db: SupplierEmployeeDto | undefined = res.data;
      if (!db) {
        enqueueWarningSnackbar(t('visitation.userWithPinNotFound'));
        await setFieldValue(`${path}.pin`, '');
        return;
      }
      if (db.name) await setFieldValue(`${path}.name`, db.name);
      if (db.lastName) await setFieldValue(`${path}.lastName`, db.lastName);
      if (db.phone) await setFieldValue(`${path}.phone`, db.phone);
      if (db.email) await setFieldValue(`${path}.email`, db.email);
      await setFieldValue(`${path}.pin`, '');
      recordSupplier(normalizeEmail(db.email), db);
    } catch {
      // Network/server failures shouldn't look like a "PIN not found" to the
      // user — surface a distinct error message instead of failing silently.
      enqueueErrorSnackbar();
    }
  };

  return (
    <Section>
      {totalCount > 1 && (
        <TitleRow>
          <CountPill>{t('invitation.create.step4VisitorsCount', { count: totalCount })}</CountPill>
        </TitleRow>
      )}

      <Input name={companyFieldName} label={t('visitation.oneTimeCompany')} />

      <FieldArray name={additionalPath}>
        {({ push, remove }) => (
          <>
            <VisitorList>
              <VisitorCard>
                {totalCount > 1 && (
                  <CardHead>
                    <CardHeadLeft>
                      <IndexChip>1</IndexChip>
                    </CardHeadLeft>
                  </CardHead>
                )}

                {showSupplierPerCard && primarySupplier && <SupplierText>{primarySupplier}</SupplierText>}

                <VisitorPersonFields
                  pathPrefix={primaryPath}
                  allowedLanguages={allowedLanguages}
                  onEmailBlur={(email) => prefillVisitorFromEmail(primaryPath, email)}
                  showPin={showPin}
                  onPinBlur={showPin ? (pin) => prefillVisitorFromPin(primaryPath, pin) : undefined}
                />
              </VisitorCard>

              {additionalVisitors.map((visitor, index) => {
                const supplier = lookupSupplierName(visitor.email);
                const rowKey = visitor._uid ?? `idx-${index}`;
                return (
                  <VisitorCard key={rowKey}>
                    <CardHead>
                      <CardHeadLeft>
                        <IndexChip>{index + 2}</IndexChip>
                      </CardHeadLeft>
                      <Tooltip title={t('invitation.create.step4RemoveVisitor')}>
                        <RemoveButton onClick={() => remove(index)} size="small">
                          <CloseRoundedIcon fontSize="small" />
                        </RemoveButton>
                      </Tooltip>
                    </CardHead>

                    {showSupplierPerCard && supplier && <SupplierText>{supplier}</SupplierText>}

                    <VisitorPersonFields
                      pathPrefix={`${additionalPath}.${index}`}
                      allowedLanguages={allowedLanguages}
                      onEmailBlur={(email) => prefillVisitorFromEmail(`${additionalPath}.${index}`, email)}
                      showPin={showPin}
                      onPinBlur={showPin ? (pin) => prefillVisitorFromPin(`${additionalPath}.${index}`, pin) : undefined}
                    />
                  </VisitorCard>
                );
              })}
            </VisitorList>

            <AddArea>
              <AddButton className="add-btn" type="button" onClick={() => push(buildEmptyVisitor())}>
                <PlusBadge className="plus-badge">
                  <AddRoundedIcon />
                </PlusBadge>
                {t('invitation.create.step4AddVisitor')}
              </AddButton>
            </AddArea>
          </>
        )}
      </FieldArray>
    </Section>
  );
};
